import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

/**
 * Obtiene el código ISO de 2 letras para PhoneInput según el país del cliente
 */
export const getCodigoIsoPais = (pais) => {
  if (!pais) return 'mx';
  const norm = pais
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
  if (norm.includes('honduras')) return 'hn';
  if (norm.includes('dominicana')) return 'do';
  if (norm.includes('guatemala')) return 'gt';
  if (norm.includes('salvador')) return 'sv';
  if (norm.includes('costa rica')) return 'cr';
  if (norm.includes('colombia')) return 'co';
  if (norm.includes('peru')) return 'pe';
  if (norm.includes('ecuador')) return 'ec';
  if (norm.includes('panama')) return 'pa';
  if (norm.includes('bolivia')) return 'bo';
  if (norm.includes('estados unidos') || norm.includes('usa')) return 'us';
  return 'mx';
};

/**
 * Limpia y convierte cualquier número de teléfono al formato exacto de WhatsApp:
 * - Solo dígitos numéricos (sin '+', sin espacios, sin guiones ni paréntesis).
 * - México (prefijo internacional 52): WhatsApp exige el prefijo '521' seguido de los 10 dígitos (ej: 5214531692683).
 *   - Si viene como 10 dígitos locales (ej: 4251267229), le antepone '521'.
 *   - Si viene como 12 dígitos (52 + 10 dígitos locales de PhoneInput), inserta el '1' -> 521...
 *   - Si ya viene con 13 dígitos y '521', se mantiene intacto.
 * - Resto de países (Honduras 504..., Guatemala 502..., RD 1..., USA 1..., etc.):
 *   - Se conservan sus dígitos internacionales puros tal como los entrega PhoneInput con su código de país.
 */
export const formatearTelefonoWhatsApp = (telefono) => {
  if (!telefono) return '';
  const soloDigitos = telefono.toString().replace(/\D/g, '');
  if (!soloDigitos) return '';

  // Caso 1: 10 dígitos (número nacional mexicano sin código de país)
  if (soloDigitos.length === 10) {
    return `521${soloDigitos}`;
  }

  // Caso 2: Número mexicano con código 52 (proveniente de PhoneInput con bandera de México o +52)
  if (soloDigitos.startsWith('52')) {
    if (soloDigitos.startsWith('521') && soloDigitos.length === 13) {
      return soloDigitos;
    }
    if (soloDigitos.length === 12) {
      return `521${soloDigitos.slice(2)}`;
    }
  }

  // Caso 3: Otros países internacionales (Honduras 504, RD 1, Guatemala 502, Colombia 57, etc.)
  // PhoneInput ya incluye el código de país exacto seleccionado por el usuario en la lista
  return soloDigitos;
};

/**
 * Valida que el teléfono tenga el formato de WhatsApp y longitud suficiente
 */
export const validarYFormatearTelefono = (telefono) => {
  if (!telefono || !telefono.toString().trim()) {
    return { valido: false, error: 'El número de teléfono no puede estar vacío.' };
  }

  const telWhatsApp = formatearTelefonoWhatsApp(telefono);
  if (!telWhatsApp || telWhatsApp.length < 8) {
    return {
      valido: false,
      error: 'El número de teléfono es demasiado corto o inválido. Seleccione el país y escriba el número completo.'
    };
  }

  return {
    valido: true,
    telefono: telWhatsApp
  };
};

export const limpiarNumeroTelefono = (t) => formatearTelefonoWhatsApp(t);
export const limpiarYFormatearWhatsApp = formatearTelefonoWhatsApp;

const TelefonosAdicionalesCliente = ({ idClienteAkasia, paisCliente, onTelefonoChange }) => {
  const { token } = useToken();
  const { showErrorToast, showSuccessToast } = useToast();

  const [telefonos, setTelefonos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Agregar en línea (sin modal)
  const [isAdding, setIsAdding] = useState(false);
  const [nuevoTelefono, setNuevoTelefono] = useState('');
  const [isSavingNew, setIsSavingNew] = useState(false);

  // Editar en línea (sin modal)
  const [editingId, setEditingId] = useState(null);
  const [editingTelefono, setEditingTelefono] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Eliminar en línea (confirmación directa sin modal)
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Cargar lista de teléfonos adicionales
  const fetchTelefonos = useCallback(async () => {
    if (!idClienteAkasia) return;
    setLoading(true);
    try {
      const response = await fetch('/api/cliente/GetTelefonosAdicionales', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ idClienteAkasia: parseInt(idClienteAkasia, 10) })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.iResponseType === 1 && data.detalles) {
          const parsed = JSON.parse(data.detalles);
          const list = parsed.Table || parsed.Table1 || [];
          setTelefonos(Array.isArray(list) ? list : []);
        } else {
          setTelefonos([]);
        }
      } else {
        setTelefonos([]);
      }
    } catch {
      showErrorToast('Error al conectar con el servidor para obtener los teléfonos adicionales.');
    } finally {
      setLoading(false);
    }
  }, [idClienteAkasia, token, showErrorToast]);

  useEffect(() => {
    fetchTelefonos();
  }, [fetchTelefonos]);

  // Guardar nuevo número en línea
  const handleSaveNew = async () => {
    const validacion = validarYFormatearTelefono(nuevoTelefono);
    if (!validacion.valido) {
      showErrorToast(validacion.error);
      return;
    }

    setIsSavingNew(true);
    try {
      const payload = {
        idTelefonoAdicional: 0,
        idClienteAkasia: parseInt(idClienteAkasia, 10),
        vTelefono: validacion.telefono
      };

      const response = await fetch('/api/cliente/GuardarTelefonoAdicional', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.iResponseType === 1) {
          showSuccessToast('Número adicional agregado');
          setIsAdding(false);
          setNuevoTelefono('');
          await fetchTelefonos();
          if (onTelefonoChange) onTelefonoChange();
        } else {
          showErrorToast(result.vMessage || result.vErrorDescription || 'Error al guardar el número');
        }
      } else {
        showErrorToast('Error en la respuesta del servidor al guardar');
      }
    } catch {
      showErrorToast('Error inesperado al guardar el número');
    } finally {
      setIsSavingNew(false);
    }
  };

  // Guardar edición en línea
  const handleSaveEdit = async (idTelefonoAdicional) => {
    const validacion = validarYFormatearTelefono(editingTelefono);
    if (!validacion.valido) {
      showErrorToast(validacion.error);
      return;
    }

    setIsSavingEdit(true);
    try {
      const payload = {
        idTelefonoAdicional: idTelefonoAdicional,
        idClienteAkasia: parseInt(idClienteAkasia, 10),
        vTelefono: validacion.telefono
      };

      const response = await fetch('/api/cliente/GuardarTelefonoAdicional', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.iResponseType === 1) {
          showSuccessToast('Número adicional actualizado');
          setEditingId(null);
          await fetchTelefonos();
          if (onTelefonoChange) onTelefonoChange();
        } else {
          showErrorToast(result.vMessage || result.vErrorDescription || 'Error al actualizar');
        }
      } else {
        showErrorToast('Error en la respuesta del servidor');
      }
    } catch {
      showErrorToast('Error inesperado al actualizar el número');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Confirmar eliminación en línea
  const handleConfirmDelete = async (idTelefonoAdicional) => {
    setIsDeleting(true);
    try {
      const response = await fetch('/api/cliente/EliminarTelefonoAdicional', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ idTelefonoAdicional })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.iResponseType === 1) {
          showSuccessToast('Número adicional eliminado');
          setDeletingId(null);
          await fetchTelefonos();
          if (onTelefonoChange) onTelefonoChange();
        } else {
          showErrorToast(result.vMessage || result.vErrorDescription || 'No se pudo eliminar el número');
        }
      } else {
        showErrorToast('Error al conectar con el servidor');
      }
    } catch {
      showErrorToast('Error inesperado al eliminar');
    } finally {
      setIsDeleting(false);
    }
  };

  const totalActivos = telefonos.length;

  return (
    <div className="telefonos-adicionales-inline">
      {/* Barra superior de conteo y botón agregar */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-1">
          <Badge bg="secondary" className="px-2 py-1">
            {totalActivos}
          </Badge>
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>adicionales</span>
        </div>
        {!isAdding && (
          <Button
            variant="outline-primary"
            size="sm"
            className="py-0 px-2"
            style={{ fontSize: '0.75rem' }}
            onClick={() => {
              setIsAdding(true);
              setNuevoTelefono('');
            }}
            title="Agregar nuevo número adicional"
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Agregar
          </Button>
        )}
      </div>

      {/* Campo para agregar directamente ahí mismo (sin modal) */}
      {isAdding && (
        <div className="mb-2 p-2 border rounded bg-white shadow-sm">
          <div className="d-flex align-items-center gap-1">
            <div className="flex-grow-1">
              <PhoneInput
                defaultCountry={getCodigoIsoPais(paisCliente)}
                value={nuevoTelefono}
                onChange={phone => setNuevoTelefono(phone)}
                inputProps={{
                  onKeyDown: e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveNew();
                    }
                    if (e.key === 'Escape') {
                      setIsAdding(false);
                      setNuevoTelefono('');
                    }
                  }
                }}
                inputClassName="form-control form-control-sm w-100"
                style={{ width: '100%' }}
                inputStyle={{ width: '100%', height: '31px', fontSize: '0.85rem' }}
                countrySelectorStyleProps={{ buttonStyle: { height: '31px' } }}
                disabled={isSavingNew}
              />
            </div>
            <Button
              variant="success"
              size="sm"
              className="py-1 px-2 text-white"
              onClick={handleSaveNew}
              disabled={isSavingNew}
              title="Guardar (Enter)"
            >
              {isSavingNew ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faCheck} />}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="py-1 px-2"
              onClick={() => {
                setIsAdding(false);
                setNuevoTelefono('');
              }}
              disabled={isSavingNew}
              title="Cancelar (Esc)"
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
          {nuevoTelefono && (
            <div className="mt-1 px-1" style={{ fontSize: '0.72rem' }}>
              <span className="text-muted">
                Formato WhatsApp a guardar: <strong className="font-monospace text-success">{formatearTelefonoWhatsApp(nuevoTelefono)}</strong>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Lista de números existentes o mensaje de vacío */}
      {loading ? (
        <div className="text-muted small py-1">
          <Spinner animation="border" size="sm" variant="primary" className="me-1" />
          Cargando...
        </div>
      ) : telefonos.length === 0 && !isAdding ? (
        <span className="text-muted small fst-italic">
          Sin números adicionales registrados
        </span>
      ) : (
        <div className="d-flex flex-column gap-1">
          {telefonos.map(tel => {
            const telRaw = (tel.vTelefono || '').replace(/[^0-9]/g, '');
            const isEditingThis = editingId === tel.idTelefonoAdicional;
            const isDeletingThis = deletingId === tel.idTelefonoAdicional;

            return (
              <div
                key={tel.idTelefonoAdicional}
                className="d-flex align-items-center justify-content-between p-1 px-2 border rounded bg-light"
                style={{ fontSize: '0.85rem' }}
              >
                {isEditingThis ? (
                  /* Campo para editar ahí mismo (sin modal) */
                  <div className="w-100">
                    <div className="d-flex align-items-center gap-1">
                      <div className="flex-grow-1">
                        <PhoneInput
                          defaultCountry={getCodigoIsoPais(paisCliente)}
                          value={editingTelefono}
                          onChange={phone => setEditingTelefono(phone)}
                          inputProps={{
                            onKeyDown: e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveEdit(tel.idTelefonoAdicional);
                              }
                              if (e.key === 'Escape') {
                                setEditingId(null);
                              }
                            }
                          }}
                          inputClassName="form-control form-control-sm w-100"
                          style={{ width: '100%' }}
                          inputStyle={{ width: '100%', height: '31px', fontSize: '0.85rem' }}
                          countrySelectorStyleProps={{ buttonStyle: { height: '31px' } }}
                          disabled={isSavingEdit}
                        />
                      </div>
                      <Button
                        variant="success"
                        size="sm"
                        className="py-1 px-2 text-white"
                        onClick={() => handleSaveEdit(tel.idTelefonoAdicional)}
                        disabled={isSavingEdit}
                        title="Guardar cambios (Enter)"
                      >
                        {isSavingEdit ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faCheck} />}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="py-1 px-2"
                        onClick={() => setEditingId(null)}
                        disabled={isSavingEdit}
                        title="Cancelar (Esc)"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                    {editingTelefono && (
                      <div className="mt-1 px-1" style={{ fontSize: '0.72rem' }}>
                        <span className="text-muted">
                          Formato WhatsApp a guardar: <strong className="font-monospace text-success">{formatearTelefonoWhatsApp(editingTelefono)}</strong>
                        </span>
                      </div>
                    )}
                  </div>
                ) : isDeletingThis ? (
                  /* Confirmación de borrado ahí mismo (sin modal) */
                  <div className="d-flex align-items-center justify-content-between w-100 text-danger" style={{ fontSize: '0.8rem' }}>
                    <span>¿Eliminar {tel.vTelefono}?</span>
                    <div className="d-flex gap-1">
                      <Button
                        variant="danger"
                        size="sm"
                        className="py-0 px-2"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => handleConfirmDelete(tel.idTelefonoAdicional)}
                        disabled={isDeleting}
                      >
                        {isDeleting ? <Spinner animation="border" size="sm" /> : 'Sí'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="py-0 px-2"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setDeletingId(null)}
                        disabled={isDeleting}
                      >
                        No
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Vista regular del número */
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-medium font-monospace">{tel.vTelefono}</span>
                      {telRaw && (
                        <a
                          href={`https://wa.me/${telRaw}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-decoration-none"
                          title="Abrir chat de WhatsApp"
                        >
                          <img width="16" height="16" src="/img/whatsapp40.png" alt="WhatsApp" />
                        </a>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 px-1 text-primary"
                        onClick={() => {
                          const rawTel = (tel.vTelefono || '').trim();
                          let formattedForInput = rawTel;
                          if (rawTel.startsWith('521') && rawTel.length === 13) {
                            formattedForInput = `+52${rawTel.slice(3)}`;
                          } else if (rawTel && !rawTel.startsWith('+')) {
                            formattedForInput = `+${rawTel}`;
                          }
                          setEditingId(tel.idTelefonoAdicional);
                          setEditingTelefono(formattedForInput);
                          setDeletingId(null);
                          setIsAdding(false);
                        }}
                        title="Editar este número"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 px-1 text-danger"
                        onClick={() => {
                          setDeletingId(tel.idTelefonoAdicional);
                          setEditingId(null);
                          setIsAdding(false);
                        }}
                        title="Eliminar este número"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

TelefonosAdicionalesCliente.propTypes = {
  idClienteAkasia: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  paisCliente: PropTypes.string,
  onTelefonoChange: PropTypes.func
};

export default TelefonosAdicionalesCliente;
